<template>
	<createReleaseLayout>
		<div class="upload-music create_music_release" :class="{'disabled': tracksUploading || tracksEdited}">

			<div class="container">

				<h3 class="page-title">
					Upload music
				</h3>

				<!-- Progress-Line -->
				<StepNavigation
					:currentStep="CURRENT_STEP"
					:id="id"
					:hasTracks="prepareUploadTracks.length > 0"
					:uploading="tracksUploading"
					:hasCoverImage="clipperAvatar"
					:changingAvatar="changeAvatarLoader"
					:isFree="publishFree"
				/>

				<!-- album details -->
				<template v-if="CURRENT_STEP === 1">
					<ReleaseInfo
						ref="releaseInfo"
						v-if="CURRENT_STEP === 1"
						:id="id"
						:editRelease="editRelease"
						:createLoading.sync="createLoading"
						:releaseInfoChanged.sync="releaseInfoChanged"
						:savedStructure="savedReleaseStructure"
						@change-step="changeCurrentStep"
						@navigate-to-created="id => $router.replace({name: 'createMusicRelease', params: {id: id}})"
						@navigate-to-create="() => $router.replace({name: 'createMusicRelease'})"
						@open-account-modal="type => { 
							viewListAccounts = type; 
							modalCreateNewAccount = true; 
							selectedTypeAccount = type; 
						}"
						@open-structure-select="handleOpenStructureSelect"
						@initialized="handleReleaseInfoInitialized"
						@update:artist-id="updateArtistId"
						@label-cleared="onLabelCleared"
						@structure-cleared="handleStructureCleared"
					/>

					<div class="step_actions">
						<button type="button" class="back disabled" v-ripple @click="changeCurrentStep(1)">Back</button>

						<!-- Next btn-->
						<button type="button" class="next primary_btn" v-ripple
						        @click="$refs.releaseInfo.saveReleaseInfo('continue')">
							<span v-if="!createLoading">Next</span>
							<v-progress-circular v-if="createLoading" :width="2" indeterminate></v-progress-circular>
						</button>

					</div>
				</template>

				<!-- tracks && Cover Image-->
				<template v-if="CURRENT_STEP === 2">

					<section class="card">
						<h2 class="name_section">Files
							<span>Drag to change order</span>
						</h2>

						<div class="body">
							<draggable v-if="prepareUploadTracks.length" v-model="prepareUploadTracks" class="list-group uploaded-tracks" :move="canMoveTrack"
							           tag="div"
							           v-bind="dragOptions" @start="isDragging = true" @end="isDragging = false"
							           @change="changeTrackPosition(prepareUploadTracks, $event)">
								<div v-for="(track, index) in prepareUploadTracks" :key="index" class="uploaded-tracks-item"
								     :class="{'upload-failure': track.isLosslessUploaded === false}">
									<div class="_name" :class="{'track_uploaded': track.id, 'track-more': artistId }">
										<span class="label">Track: {{ index + 1 }}</span>

										<input type="text" class="value" v-model="track.title" @input="handleMetadataChange(index)"
										       :readonly="tracksUploading || tracksEdited || CREATED_ALBUM.beenPublished || false">
										<v-progress-linear
											v-if="track.progress > 0"
											v-model="track.progress"
											color="#20C4F5"
											height="2">
										</v-progress-linear>
										<div v-if="track.id && track.isLosslessUploaded" class="is_uploaded_track"></div>

										<div class="upload_status">
											<span class="progress" v-if="!track.success && track.progress">{{ track.progress }}%</span>
											<span class="upload_success" v-if="track.success === true"></span>
											<span class="delete_track" @click="confirmDeleteTrack(index, track.id)"
                                                  :class="{'delete-process': trackDeleteProgress(track.id)}"
											      v-if="(editRelease !== true || !CREATED_ALBUM.beenPublished) && uploadedTracks && !track.progress">
                                                <v-progress-circular v-if="trackDeleteProgress(track.id)" :width="1" indeterminate></v-progress-circular>
                                            </span>
										</div>
									</div>
									<div class="_artist" :class="{'field_error': showTrackArtistError(index)}">
										<span class="label">Artist</span>
										<span class="value">
                                            <input type="text" required v-model="track.trackArtist" @input="handleMetadataChange(index)"
                                                   :readonly="tracksUploading || tracksEdited || CREATED_ALBUM.beenPublished || false"
                                                   @click="openArtistDialogForTrack(index)" @focus="openArtistDialogForTrack(index)" tabindex="0">
                                            <span class="error-msg" v-if="showTrackArtistError(index)">Artist is required.</span>
										</span>
									</div>

									<div class="_bpm">
										<span class="label">BPM</span>
										<span class="value">
                                            <input type="text" required v-on:keypress="numbersOnly" v-model="track.bpm" @input="handleMetadataChange(index)">
                                        </span>
									</div>
									<div class="_key">
										<span class="label">Key</span>
										<span class="value" @click="showKeys(track, index)" @focus="showKeys(track, index)" tabindex="0">
                                            <input type="text" :value="getKeyCode(track)" id="track-key" readonly>
                                        </span>
									</div>
								</div>
							</draggable>

							<div class="wrap_keys filter_keys" v-if="prepareUploadTracks.length && showTrackKeysPanel" v-click-outside="onClickOutsideKeys"
							     :style="{position: 'absolute', 'top': positionKeysPanel + 'px'}">
								<div class="b_section">
									<div v-for="key in songKeysGroupB" :key="key.code" @click="select_key(key)"
									     :class="{'active': isSelectedKey(showTrackKeysPanelIndex, key)}">
										<div class="_mark">{{ key.code }}</div>
										<div class="_name">{{ key.name }}</div>
									</div>
								</div>
								<div class="a_section">
									<div v-for="key in songKeysGroupA" :key="key.code" @click="select_key(key)"
									     :class="{'active': isSelectedKey(showTrackKeysPanelIndex, key)}">
										<div class="_mark">{{ key.code }}</div>
										<div class="_name">{{ key.name }}</div>
									</div>
								</div>
							</div>

							<div class="upload-controls" v-if="editRelease !== true || !CREATED_ALBUM.beenPublished">
								<input
									type="file"
									hidden
									multiple
									ref="uploadForm"
									accept=".wav, .flac"
									@change="handleTracksUpload($event)">
								<button type="button" v-ripple class="primary_btn large" @click="selectTrackFile">Select</button>
								<span class="description_">200 MB limit per track <br>WAV, FLAC<br>
                                    <span class="error-msg tracks" v-if="$v.prepareUploadTracks.$error">*Required.</span>
                                </span>

							</div>
						</div>
					</section>

					<section class="card">
						<h2 class="name_section">Cover</h2>
						<div class="body">
							<CoverImageUploader
								:album-image="albumDetails.smallImage"
								:validation-error="$v.clipperAvatar.$error"
								:clipper-avatar.sync="clipperAvatar"
								:valid-image.sync="validImage"
								:scale-image-width="scaleImageWidth"
								:scale-image-height="scaleImageHeight"
								:upload-image-loader="uploadImageLoader"
								:clipper-dialog.sync="clipperDialog"
								@photo-upload="photoUpload"
								@close-dialog="closeClipperDialog"
								@change-image="changeAva"
								@clip-image-complete="handleClippedImage"
								@update-dimensions="updateDimensions"

								ref="coverImageUploader"
							/>
							<div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>
						</div>
					</section>

					<div class="step_actions">
						<button type="button" class="back" v-ripple @click="changeCurrentStep(1)">Back</button>

						<button type="button" class="next primary_btn" v-ripple @click="saveFiles('continue')">
							<span v-if="!createLoading">Next</span>
							<v-progress-circular v-if="createLoading" :width="2" indeterminate></v-progress-circular>
						</button>

					</div>
				</template>

				<!-- Commerce -->
				<template v-if="CURRENT_STEP === 3">
					<CommerceSection
						:id="id"
						:publishFree.sync="publishFree"
						:albumPrice.sync="albumPrice"
						:allowTracksChecked.sync="allowTracksChecked"
						:createdAlbum="CREATED_ALBUM"
						:musicReleaseCommerce="MUSIC_RELEASE_COMMERCE"
						:musicReleaseCommerceLoader="MUSIC_RELEASE_COMMERCE_LOADER"
						:stripeAccount="STRIPE_ACCOUNT"
						:loaderGetStripeAccount="LOADER_GET_STRIPE_ACCOUNT"
						:loaderGetStripeAccountLink="LOADER_GET_STRIPE_ACCOUNT_LINK"
						:createLinkStripeAccount="CREATE_LINK_STRIPE_ACCOUNT"
						:stripeAccountLink="STRIPE_ACCOUNT_LINK"
						:facebook="FACEBOOK"
						:createLoading="createLoading"
						@go-back="changeCurrentStep(2)"
						@save-commerce-start="createLoading = true"
						@save-commerce-complete="handleSaveCommerceComplete"
						@save-commerce-error="handleSaveCommerceError"
					/>
				</template>


				<!-- DIALOGS -->
				<newAccount v-if="modalCreateNewAccount" v-model="modalCreateNewAccount" :uses="'createRelease'" :viewListAccounts="viewListAccounts"></newAccount>
				<comingSoonDialog v-if="comingSoonDialogDialog" v-model="comingSoonDialogDialog"></comingSoonDialog>
				<warningChangeAlbum v-if="warningChangeAlbumDialog" v-model="warningChangeAlbumDialog" :albumID="id"></warningChangeAlbum>
				<TrackArtistSelector 
					v-if="trackArtistSelectorVisible"
					v-model="trackArtistSelectorVisible"
					:trackIndex="trackArtistSelectorIndex"
					:trackTitle="trackArtistSelectorIndex !== null ? prepareUploadTracks[trackArtistSelectorIndex].title : ''"
					:albumArtists="getAlbumArtistsForTrack()"
					:selectedTrackArtists="trackArtistSelectorIndex !== null ? getTrackArtists(trackArtistSelectorIndex) : []"
					@artists-selected="handleTrackArtistsSelected"
				></TrackArtistSelector>

				<!-- Centralized Dialogs Component -->
				<release-dialogs
					:photo-limit-dialog-visible.sync="photoLimitDialog"
					:track-limit-dialog-visible.sync="trackLimitDialog"
					:upload-limit-dialog-visible.sync="uploadLimitDialog"
					:warning-remove-track-dialog-visible.sync="warningRemoveTrackDialog"
					:lossless-uploaded-failed-dialog-visible.sync="losslessUploadedFailedDialog"
					:is-offline-visible.sync="isOffline"
					:structure-dialog-visible.sync="structureDialogVisible"
					:seconds-remaining="secondsRemaining"
					:track-delete-progress-id="trackDeleteProgressId"
					:allow-remove-tracks-index="allowRemoveTracksIndex"
					:discord-link="DISCORD"
					:facebook-link="FACEBOOK"
					:initial-structure="savedReleaseStructure"
					@select-new-photo="changeAva"
					@confirm-remove-track="deleteTrack"
					@retry-connection="stopTimer"
					@structure-selected="handleStructureSelected"
				/>
			</div>
		</div>
	</createReleaseLayout>
</template>

<script>
import axios from "axios";
import {mapActions, mapGetters, mapMutations} from "vuex";
import {maxLength, required, minLength} from "vuelidate/lib/validators";
import draggable from 'vuedraggable';
import {blockTitleInput, blockDescriptionTextarea, numbersOnly, keyHandler} from "@/utils/formValidators";
import {formatDate, convertToDatePicker, getDay} from "@/utils/dateFormatters";

import createReleaseLayout from "@/layouts/create-release-layout.vue";
import {baseUrlToBlob} from "@/utils/baseUrlToBlob";
import albumDetails from "@/pages/musicRelease/albumDetails.vue";
import login from "@/pages/auth/login.vue";
import {songKeysGroupA, songKeysGroupB} from "@/store/modules/metaData/getters";
import de from "vue2-datepicker/locale/es/de";
import StepNavigation from "@/components/musicRelease/StepNavigation.vue";
import CoverImageUploader from "@/components/musicRelease/CoverImageUploader.vue";
import CommerceSection from "@/components/musicRelease/CommerceSection.vue";
import ReleaseInfo from "@/components/musicRelease/ReleaseInfo.vue";
import ReleaseDialogs from "@/components/musicRelease/ReleaseDialogs.vue";
import TrackArtistSelector from "@/components/TrackArtistSelector.vue";

const comingSoonDialog = () => import('@/components/dialogs/comingSoonDialog');
const warningChangeAlbum = () => import('@/components/dialogs/warningСhangeAlbum');
const newAccount = () => import('@/components/newAccount.vue');

export default {
	name: "createMusicRelease",
	props: ['id', 'editRelease', 'create'],
	components: {
		createReleaseLayout,
		draggable,
		comingSoonDialog,
		warningChangeAlbum,
		newAccount,
		StepNavigation,
		CoverImageUploader,
		CommerceSection,
		ReleaseInfo,
		ReleaseDialogs,
		'release-dialogs': ReleaseDialogs, // Add kebab-case registration
		TrackArtistSelector,
	},
	data(vm) {
		return {
			// upload tracks
			tracksUploading: false,
			tracksEdited: false,
			tracksDeleted: false,
			prepareUploadTracks: [],
			uploadedTracks: [],

			// Artist ID from ReleaseInfo component
			artistId: null,
			mainArtistName: null, // Add main artist name

			// upload image
			clipperDialog: false,
			clipperDialogVisible: false,
			uploadedFile: '',
			clipperAvatar: '',
			changeAvatarLoader: false,
			validImage: true,
			photoLimitDialog: false,
			uploadImageLoader: false,
			scaleImageWidth: null,
			scaleImageHeight: null,

            online: navigator.onLine,


			// albumDetails
			details: {
				title: '',
				description: '',
				tags: [],
				genre: {id: 2, name: 'Psychedelic Trance'},
				subgenre: {id: '', name: ''},
				artist: {id: '', name: ''},
				label: {id: '', title: ''},
			},
			releaseInfoChanged: false,
			artistSubscriptionError: false,
            trackDeleteProgressId: undefined,

			genresDropdown: false,
			subGenresDropDown: false,
			artistDropDown: false,

			genresList: '',
			allSubGenresList: '',
			subGenresList: '',
			artistsList: '',
			recordLabelsList: '',

			newTag: '',

			// Release Date Picker
			datePicker: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
			dateFormatted: vm.formatDate((new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)),
			releaseDateDatePicker: false,
			daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],

			publishFree: false,
			allowTracksChecked: false,
			albumPrice: null,
			formValid: false,
			createLoading: false,
			validCommence: false,

			variantDistribution: '',

			drag: false,

			comingSoonDialogDialog: false,
			warningChangeAlbumDialog: false,
			modalCreateNewAccount: false,
			albumPriceLimitDialog: false,
			trackPriceLimitDialog: false,
			userDecidesPriceDialog: false,
			priceDistributionDialog: false,
			uploadLimitDialog: false,
			trackLimitDialog: false,
            losslessUploadedFailedDialog: false,
            isConnected: true,

			warningRemoveTrackDialog: false,
			allowRemoveTracksIndex: null,

			warningChangeArtistDialog: false,
			allowChangeArtist: false,


			showTrackKeysPanel: null,
			showTrackKeysPanelIndex: null,
			positionKeysPanel: null,

            //
            secondsRemaining: 15,
            intervalId: null,

			//
			accounts: {
				artist: null,
				label: null,
			},
			viewListAccounts: null, // label or artist

			errorMessage: '', // Error message for UI display
			_lastScaleValue: 0,
			_updateInProgress: false,
			_tracksProcessingComplete: true,
			_imageProcessingComplete: true,
			_onlineStatusTimeout: null,
			_totalTracksToProcess: 0,
			_tracksToProcess: 0,
            editingTrackArtistIndex: null, // Index of track whose artist is being edited via dialog

            // Track if label was explicitly cleared by user
            labelExplicitlyCleared: false,

            // New properties for track artist selection
            trackArtistSelectorVisible: false,
            trackArtistSelectorIndex: null,
            structureDialogVisible: false,
			savedReleaseStructure: null,
		}
	},
	validations: {
		prepareUploadTracks: {
			required,
			minLength: minLength(1),
			$each: {
				trackArtist: { required }
			}
		},
		clipperAvatar: {
			required
		},
		details: {
			title: {
				required,
				maxLength: maxLength(50)
			},
			description: {
				maxLength: maxLength(2000)
			},
			tags: {
				required
			},
			genre: {
				id: {
					required,
				}
			},
			subgenre: {
				id: {
					required,
				}
			},
			artist: {
				name: {
					required
				}
			}
		},

		datePicker: {
			required
		},
		publishFree: {
			checked(val) {
				return val
			},
		},

	},
	watch: {
		id() {
			if (!this.id) {
				this.changeCurrentStep(1);
				this.details = {
					title: '',
					description: '',
					tags: [],
					genre: {id: '', name: ''},
					subgenre: {id: '', name: ''},
					artist: {id: '', name: ''},
					label: {id: '', title: ''}
				};
				this.prepareUploadTracks = [];
				this.clipperAvatar = '';
			} else {
				this.updateAlbumData();
			}
		},
		'details.title': {
			handler(oldVal, newVal) {
				if (oldVal !== newVal) {
					if (oldVal && newVal) {
						if (oldVal.length > 51) {
							return
						}
						this.releaseInfoChanged = true;
						this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', '');

					}
				}
			},
			deep: true
		},
		'details.description': {
			handler(oldVal, newVal) {
				if (oldVal !== newVal) {
					if (oldVal && newVal) {
						this.releaseInfoChanged = true;
						this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', '');
					}
				}
			},
			deep: true
		},

		'details.subgenre.id': {
			handler(newVal, oldVal) {
				if (this.CREATED_ALBUM && this.CREATED_ALBUM.subGenres && !this.CREATED_ALBUM.subGenres.length) {
					this.releaseInfoChanged = true;
				}

				if (oldVal !== this.details.subgenre.id) {
					if (oldVal) {
						this.releaseInfoChanged = true;
						this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', '');
					}
				}
			},
			deep: true
		},

		datePicker() {
			this.dateFormatted = this.formatDate(this.datePicker);
			this.releaseInfoChanged = true;
			this.releaseDateDatePicker = false;
			this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', '');
		},

		SELECT_ARTIST() {
			// If we're editing a specific track's artist (in Various Artists mode)
			if (this.SELECT_ARTIST && this.editingTrackArtistIndex !== null) {
				// Update the specific track's artist name
				const trackIndex = this.editingTrackArtistIndex;
				if (trackIndex >= 0 && trackIndex < this.prepareUploadTracks.length) {
					this.$set(this.prepareUploadTracks[trackIndex], 'trackArtist', this.SELECT_ARTIST.name);
					console.log(`Updated track ${trackIndex} artist to: ${this.SELECT_ARTIST.name}`);
					this.handleMetadataChange(trackIndex); // Mark for metadata update
				}
				this.editingTrackArtistIndex = null; // Reset the index
			} 
			// If we're selecting the main artist for the release (not in Various Artists mode)
			else if (this.SELECT_ARTIST) {
				// Update the main artist
				this.details.artist.id = this.SELECT_ARTIST.id;
				this.details.artist.name = this.SELECT_ARTIST.name;
				this.artistSubscriptionError = false;
				
				// Note: The artistId watcher will handle updating track artist names
			}
		},
		modalCreateNewAccount(newVal) {
			// If the dialog is closed and we were editing a track's artist, reset the index
			if (!newVal && this.editingTrackArtistIndex !== null) {
				console.log('Artist dialog closed without selection, resetting track index.');
				this.editingTrackArtistIndex = null;
			}
		},

		SELECTED_LABEL(newValue) {
			console.log('Main component: SELECTED_LABEL watcher triggered:', newValue);
			
			if (newValue) {
				// Handle selection of a label
				console.log('Main component: SELECTED_LABEL changed to:', newValue);
				this.details.label.id = newValue.id;
				this.details.label.title = newValue.title;
				this.releaseInfoChanged = true; // Mark as changed so it will be saved
				
				// Reset the explicit clearing flag when a label is selected
				console.log('Label selected, resetting labelExplicitlyCleared flag');
				this.labelExplicitlyCleared = false;
			} else if (newValue === null) {
				// Handle explicit null case (clearing the label)
				console.log('Main component: SELECTED_LABEL cleared to null');
				this.details.label.id = '';
				this.details.label.title = '';
				this.releaseInfoChanged = true; // Mark as changed so it will be saved
			}
		},

		WARNING_CHANGE_STATUS() {
			if (this.WARNING_CHANGE_STATUS === true) {
				this.$refs.uploadForm.click();
				this.$store.commit('SET_WARNING_CHANGE_STATUS', false);
			}
		},
		scale(val) {
			console.log('Scale watcher triggered with value:', val);
			// Avoid unnecessary recursion
			if (val === this._lastScaleValue) {
				console.log('Skipping duplicate scale update');
				return;
			}

			// Validate scale bounds
			let newScale = val;
			if (!newScale || newScale < 0.5) {
				newScale = 0.5;
			}
			if (newScale > 2) {
				newScale = 2;
			}

			// Store the value we're processing to avoid recursion
			this._lastScaleValue = newScale;

			// Only update the model if the value actually changed
			if (newScale !== val) {
				console.log('Correcting scale from', val, 'to', newScale);
				this.scale = newScale;
				return; // The watcher will be called again with the corrected value
			}

			try {
				// Use the getClipper method to access the clipper
				const clipperComponent = this.$refs.coverImageUploader;
				if (clipperComponent) {
					// Delegate actual scaling to child component
					clipperComponent.handleScale(newScale);
				} else {
					console.warn('Clipper component not found');
				}
			} catch (error) {
				console.error('Error updating zoom scale:', error);
				// Display error to user
				this.$set(this, 'errorMessage', 'Error updating image zoom. Please try again.');
				// Clear error after 3 seconds
				setTimeout(() => {
					this.$set(this, 'errorMessage', '');
				}, 3000);
			}
		},
		clipperDialog() {

		},
		CURRENT_STEP() {
			if (this.CURRENT_STEP === 3) {
				if (!this.STRIPE_ACCOUNT) {
					this.getStripeData();
				}
			}
		},
		albumPrice() {
			this.releaseInfoChanged = true;
		},
        isConnected() {
            if (this.isConnected) {
                this.updateAlbumData();
            }
        },
        isOffline() {
            this.isOffline ? this.stopTimer() : this.startTimer();
        },
        artistId(newId) {
            console.log('artistId watcher triggered with:', newId);
            
            // Clear mainArtistName if newId is null or undefined
            if (!newId) {
                this.mainArtistName = null;
                console.log('Main artist cleared due to null/undefined artistId');
                return;
            }
            
            // If we're in "Various Artists" mode and an artist is selected,
            // we should switch back to single artist mode
            if (this.VARIOUS_ARTISTS && newId) {
                console.log('Artist selected while in Various Artists mode, switching to single artist mode');
                this.$store.commit('SET_VARIOUS_ARTISTS', false);
            }
            
            // Find the artist object from the list populated by GET_YOUR_ARTISTS
            const mainArtist = this.YOUR_ARTISTS.find(a => a.id === newId);
            if (mainArtist) {
                this.mainArtistName = mainArtist.name;
                console.log(`Main artist set to: ${this.mainArtistName}`);
                
                // Update track artists based on the mode
                if (!this.VARIOUS_ARTISTS) {
                    // Process all tracks to ensure the album artist is properly applied
                    // and any duplicate artists are removed
                    this.updateAllTrackArtistNames();
                } else {
                    // If using various artists, only update empty tracks
                    this.updateEmptyTrackArtistNames();
                }
            } else {
                // If artistsList isn't loaded yet, try to get the artist details directly
                if (newId) { // Double-check that we have a valid ID to fetch
                    this.GET_ARTIST(newId)
                        .then(response => {
                            if (response && response.name) {
                                this.mainArtistName = response.name;
                                console.log(`Main artist set from API: ${this.mainArtistName}`);
                                
                                // Update track artists based on the mode
                                if (!this.VARIOUS_ARTISTS) {
                                    // Process all tracks to ensure the album artist is properly applied
                                    // and any duplicate artists are removed
                                    this.updateAllTrackArtistNames();
                                } else {
                                    // If using various artists, only update empty tracks
                                    this.updateEmptyTrackArtistNames();
                                }
                            }
                        })
                        .catch(err => {
                            console.error(`Failed to get artist details: ${err}`);
                        });
                } else {
                    console.log('Not fetching artist details - artist ID is null or undefined');
                }
            }
        },
		// Add a watcher for VARIOUS_ARTISTS
		VARIOUS_ARTISTS(newValue) {
			console.log('VARIOUS_ARTISTS changed to:', newValue);
			if (newValue === true) {
				// When switching to Various Artists mode, clear only the main artist
				this.details.artist.id = '';
				this.details.artist.name = '';
				this.mainArtistName = '';
				
				// Also clear the artist in the store
				this.$store.commit('SET_SELECT_ARTIST', null);
				
				// DO NOT clear track artists - they should be preserved
				console.log('Various Artists mode enabled - cleared main artist but preserving track artists');
			} else {
				// In single artist mode, update all tracks to match the main artist if available
				if (this.mainArtistName) {
					console.log('Single artist mode enabled - updating all tracks to match main artist');
					this.updateAllTrackArtistNames();
				}
			}
		},
	},
	computed: {
		de() {
			return de
		},
		albumDetails() {
			return albumDetails
		},
		...mapGetters([
			'CREATED_ALBUM', 'CREATED_ALBUM_LOADING',

			'userID', 'isAdmin', 'GENRES', 'YOUR_ARTISTS', 'YOUR_RECORD_LABELS', 'PREPARE_TRACK', 'FILE_URL',
			'TEMP_CREATED_ALBUMS_ID', 'TEMP_PLAYLIST_ID', 'ARTIST',

			'ALBUM_IMAGE_STATUS',
			'UPLOAD_TRACK',
			'CURRENT_STEP',
			'CHANGE_ALBUM',
			'WARNING_CHANGE_STATUS',
			'RELEASE_INFO_ERROR_MESSAGE',
			'FROM_PAGE',

			// 'SELECTED_ARTIST_ID',
			'CHANGE_SELECTED_ARTIST',
			'CHANGE_SELECTED_LABEL',
			'SELECT_ARTIST',
			'SELECTED_LABEL',
			'VARIOUS_ARTISTS',

			'FACEBOOK',
			'DISCORD',

			// commerce && stripe
			'MUSIC_RELEASE_COMMERCE',
			'MUSIC_RELEASE_COMMERCE_LOADER',
			'STRIPE_ACCOUNT',
			'CREATE_LINK_STRIPE_ACCOUNT',
			'STRIPE_ACCOUNT_LINK',
			'LOADER_GET_STRIPE_ACCOUNT',
			'LOADER_GET_STRIPE_ACCOUNT_LINK',

            //Song Keys Getters
            'songKeysGroupA',
            'songKeysGroupB'
		]),
		dragOptions() {
			return {
				animation: 200,
				group: "description",
				disabled: this.tracksUploading || this.tracksEdited, // Updated disabled state
				ghostClass: "ghost"
			};
		},

        isOffline() {
            return !this.isConnected
        }
	},
	created() {
		if (this.$route.params.id) {
			// this.mode = 'edit';
		} else {
			// this.mode = 'create';
		}
		document.addEventListener("visibilitychange", this.handleVisibilityChange);
        window.addEventListener('online', this.updateOnlineStatus);
        window.addEventListener('offline', this.updateOnlineStatus);

		this.$watch(
			() => this.$route.params.id,
			(newId, oldId) => {
				if (newId === undefined && oldId !== undefined) {
					// mode create release
					this.changeCurrentStep(1);
					this.$store.commit('SET_TEMP_CREATED_ALBUMS_ID', null);
					this.$store.commit('SET_CREATED_ALBUM', '');
				}
			}
		);
		if (this.$route.query.redirect_status === 'succeeded') {
			this.modalCreateNewAccount = true;
		}
		if (this.FROM_PAGE.name === 'albumDetails' && this.CURRENT_STEP === 3) {
			this.changeCurrentStep(3);
			if (!this.STRIPE_ACCOUNT) {
				this.getStripeData();
			}
		} else {
			this.changeCurrentStep(1); // 1
		}
		this.$store.commit('SET_WARNING_CHANGE_STATUS', false);
		this.getGenres();

		this.GET_YOUR_ARTISTS({owner: this.userID, deletedAt: false, 'order[createdAt]': 'desc'})
			.then(() => {
				this.artistsList = this.YOUR_ARTISTS;
			})
			.catch(err => console.error(`GET_YOUR_ARTISTS, ${err}`));

		this.GET_YOUR_RECORD_LABELS({
			owner: this.userID,
			sort: 'createdAt',
			order: 'desc'
		})
			.then(() => {
				this.recordLabelsList = this.YOUR_RECORD_LABELS;
			})
			.catch(err => console.error(`GET_YOUR_RECORD_LABELS, ${err}`));

		if (this.id) {
			this.updateAlbumData();
		}

		this.$on('retryUploadTrack', (params) => {
			if (params.isReUpload) {
				this.prepareUploadTracks[params.trackIndex].isLosslessUploaded = false;
                this.tracksUploading = false;
				return;
			}

			// Call storeTrack with the updated Promise-based approach
			this.storeTrack(params.trackFile, params.trackIndex, true)
			    .then(() => {
			        console.log(`Successfully retried upload for track at index ${params.trackIndex}`);
			    })
			    .catch(err => {
			        console.error(`Failed to retry upload for track at index ${params.trackIndex}:`, err);
			    });
		});

        this.loadSongKeys();
	},
	mounted() {
		if (!this.id) {
			this.changeCurrentStep(1);
			this.$store.commit('SET_TEMP_CREATED_ALBUMS_ID', null);
			this.$store.commit('SET_CREATED_ALBUM', '');
		}
		const currentFragment = window.location.hash;
		if (currentFragment.includes("#step=3")) {
			this.getStripeData();
			this.changeCurrentStep(3);
		}
	},
	destroyed() {
		document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        window.removeEventListener('online', this.updateOnlineStatus);
        window.removeEventListener('offline', this.updateOnlineStatus);

        this.stopTimer();
	},
	methods: {
		...mapActions([
			'GET_CREATED_ALBUM', 'GET_GENRES', 'GET_YOUR_ARTISTS', 'GET_YOUR_RECORD_LABELS',
			'ADD_MUSIC_RELEASE', 'EDIT_MUSIC_RELEASE', 'CHANGE_ALBUM_STATUS',
			'ADD_SONGS', 'EDIT_SONG', 'REMOVE_SONG', 'UPDATE_SONG',
			'ADD_ALBUM_IMAGE', 'GET_ARTIST',

			// commerce && stripe
			'POST_MUSIC_RELEASE_COMMERCE', 'GET_MUSIC_RELEASE_COMMERCE',
			'GET_STRIPE_ACCOUNT', 'GET_LINK_STRIPE_ACCOUNT', 'CREATE_STRIPE_ACCOUNT',

            'loadSongKeys',
		]),
		// Add imported validator functions to methods
		blockTitleInput,
		blockDescriptionTextarea,
		numbersOnly,
		keyHandler,

		// Add imported date formatter functions to methods
		formatDate,
		convertToDatePicker,
		getDay,

		handleVisibilityChange() {
			this.isActiveTab = !document.hidden;
			if (this.isActiveTab) {
				this.getStripeData();
			}
		},
        updateOnlineStatus() {
            this.online = navigator.onLine;
            this.isConnected = this.online;

            if (this.isConnected && this.id) {
                // Use a longer delay to avoid triggering updates too rapidly
                clearTimeout(this._onlineStatusTimeout);
                this._onlineStatusTimeout = setTimeout(() => {
                    this.updateAlbumData();
                }, 2000);
            }
        },

		showKeys(track, index) {
			if (this.tracksUploading || this.tracksEdited) { // Add check here
				return;
			}
			this.showTrackKeysPanelIndex = index;
			this.showTrackKeysPanel = true;
			this.positionKeysPanel = (index * 70) + 70;
        },
        getKeyCode(track) {
            if (track?.key?.code) {
                return track.key.code;
            }

            return null;
        },
        isSelectedKey(showTrackKeysPanelIndex, key) {
            if (this.prepareUploadTracks[showTrackKeysPanelIndex]?.key?.id === key.id) {
                return true;
            }

            return false;
        },
		select_key(key) {
			this.prepareUploadTracks[this.showTrackKeysPanelIndex].key = key;
			this.handleMetadataChange(this.showTrackKeysPanelIndex); // Call handler here
			this.showTrackKeysPanel = false;
		},
		onClickOutsideKeys: function (event) {
			if (event.target.id !== 'track-key') {
				this.showTrackKeysPanel = false;
			}
		},
		confirmChangeAccount() { // artist
			this.viewListAccounts = 'Artist';
			this.$store.commit('SET_SELECT_ARTIST', null);
			
			// Always show the structure choice dialog
			this.modalCreateNewAccount = true;
		},
		confirmChangeLabel() { // record-label
			this.viewListAccounts = 'Label';
			this.modalCreateNewAccount = true;
		},
		setAccount() {
			this.viewListAccounts = null;
			this.modalCreateNewAccount = true;
		},
		clearInput(el) {
			this.$store.commit('SET_VARIOUS_ARTISTS', false);
			if (el === 'label') {
				this.details.label.id = '';
				this.details.label.title = '';

				this.$store.commit('SET_SELECTED_LABEL', null);
			}
			if (el === 'artist') {
				this.details.artist.id = '';
				this.details.artist.name = '';
				this.$store.commit('SET_SELECT_ARTIST', null);
			}
		},

		getGenres() {
			this.GET_GENRES()
				.then(() => {
					this.genresList = this.GENRES;
				})
				.catch(err => console.error(`getGenres, ${err}`))
				.finally(() => {
					if (this.genresList.length && this.details.genre.id) {
						let subgenreList = this.genresList.find(el => el.id === this.details.genre.id);
						this.subGenresList = subgenreList.subGenres;
					}
				})
		},

		updateAlbumData() {
			// Prevent multiple simultaneous calls
			if (this._updateInProgress) {
				console.log('Album update already in progress, ignoring duplicate call');
				return;
			}

			this._updateInProgress = true;

			this.GET_CREATED_ALBUM(this.id)
				.then(() => {
					console.log('Album data received:', this.CREATED_ALBUM);
					
					this.details.title = this.CREATED_ALBUM.title;
					this.details.description = !this.CREATED_ALBUM.description ? '' : this.CREATED_ALBUM.description;
					this.details.tags = !this.CREATED_ALBUM.tags ? [] : this.CREATED_ALBUM.tags;

					// Determine structure based on variousArtists flag and existing selection
					const isVariousArtists = this.CREATED_ALBUM.variousArtists === true || this.CREATED_ALBUM.variousArtists === 1;
					
					// Only update the structure if we don't already have a saved structure
					// or if the structure is inconsistent with the variousArtists flag
					if (!this.savedReleaseStructure || 
						(isVariousArtists && this.savedReleaseStructure !== 'Various Artists Release') ||
						(!isVariousArtists && this.savedReleaseStructure === 'Various Artists Release')) {
						
						// If we have secondary artists, it might be a Multi-Artist Release
						const hasSecondaryArtists = this.CREATED_ALBUM.secondaryArtists && this.CREATED_ALBUM.secondaryArtists.length > 0;
						
						if (isVariousArtists) {
							this.savedReleaseStructure = 'Various Artists Release';
						} else if (hasSecondaryArtists) {
							this.savedReleaseStructure = 'Multi-Artist Release';
						} else {
							this.savedReleaseStructure = 'Single Artist Release';
						}
						
						console.log('Updated structure from album data:', this.savedReleaseStructure);
					} else {
						console.log('Preserving existing structure selection:', this.savedReleaseStructure);
					}
					
					// Update the variousArtists flag based on the structure
					if (this.savedReleaseStructure === 'Various Artists Release') {
						this.$store.commit('SET_VARIOUS_ARTISTS', true);
					} else {
						this.$store.commit('SET_VARIOUS_ARTISTS', false);
					}
					
					// Set structure on ReleaseInfo component if it exists
					this.$nextTick(() => {
						if (this.$refs.releaseInfo) {
							console.log('Setting structure on ReleaseInfo component:', this.savedReleaseStructure);
							this.$refs.releaseInfo.releaseStructure = this.savedReleaseStructure;
						}
					});

					if (this.CREATED_ALBUM.artist) {
						this.details.artist.id = this.CREATED_ALBUM.artist.id;
						this.details.artist.name = this.CREATED_ALBUM.artist.name;
						this.mainArtistName = this.CREATED_ALBUM.artist.name;
					} else {
						this.mainArtistName = null;
					}

					// Process tracks
					this.prepareUploadTracks = [];
					if (this.CREATED_ALBUM.playList && this.CREATED_ALBUM.playList.length) {
						console.log('Processing playlist with', this.CREATED_ALBUM.playList.length, 'tracks');
						
						for (let track of this.CREATED_ALBUM.playList) {
							if (!track.song.deletedAt) {
								console.log('Processing track:', track.song.title);
								console.log('Track data:', track.song);
								
								// Set position
								track.song.position = track.position;

								// Handle track artist - check both song.artist and song.trackArtist
								if (track.song.artist && track.song.artist.name) {
									console.log(`Found artist in song.artist: ${track.song.artist.name}`);
									track.song.trackArtist = track.song.artist.name;
								} else if (track.song.trackArtist) {
									console.log(`Using existing trackArtist: ${track.song.trackArtist}`);
									// Keep existing trackArtist
								} else if (!this.VARIOUS_ARTISTS && this.mainArtistName) {
									console.log(`Using main artist name: ${this.mainArtistName}`);
									track.song.trackArtist = this.mainArtistName;
								} else {
									console.log('No artist found for track');
									track.song.trackArtist = '';
								}

								this.prepareUploadTracks.push(track.song);
							}
						}
						
						// Sort tracks by position
						this.prepareUploadTracks.sort((a, b) => (a.position > b.position) ? 1 : -1);
						
						console.log('Final prepareUploadTracks:', this.prepareUploadTracks.map(t => ({
							title: t.title,
							trackArtist: t.trackArtist,
							position: t.position
						})));
					}

					// Rest of the existing code...

					// Only set clipperAvatar if it's not already set
					if (!this.clipperAvatar) {
						this.clipperAvatar = !this.CREATED_ALBUM.bigImage ? '' : this.CREATED_ALBUM.bigImage;
					}

					// Make sure all tracks have artist names
					this.updateEmptyTrackArtistNames();

					this.createLoading = false;
					this.tracksUploading = false;
				})
				.catch(err => {
					console.error(`none release id= ${this.id}, error: ${err}`);
					this.$router.replace({name: 'createMusicRelease'});
				})
				.finally(() => {
					// Reset flag to allow future updates
					setTimeout(() => {
						this._updateInProgress = false;
					}, 1000); // Add a small delay to prevent rapid sequential calls
				});

			this.GET_MUSIC_RELEASE_COMMERCE({id: this.id})
				.then(() => {
					this.publishFree = this.MUSIC_RELEASE_COMMERCE.is_free === true;
					this.albumPrice = this.MUSIC_RELEASE_COMMERCE.price !== null ? this.MUSIC_RELEASE_COMMERCE.price.toFixed(2) : null;

					if (this.MUSIC_RELEASE_COMMERCE.songs.length) {
						for (let song of this.MUSIC_RELEASE_COMMERCE.songs) {
							if (song.price !== null) {
								song.price = song.price.toFixed(2);
								this.allowTracksChecked = true;
							}
						}
					}
				})
				.catch(err => console.error(`GET_MUSIC_RELEASE_COMMERCE, ${err}`));
		},
		changeTrackPosition: function (tracks, $event) {
			const newPosition = $event.moved.newIndex;
			const updateTrackPosition = tracks[newPosition];
			updateTrackPosition.position = newPosition + 1;
			for (let track of tracks) {
				this.releaseInfoChanged = true;
				track.position = tracks.indexOf(track) + 1;

				// Mark this track as having metadata changes that need to be saved
				if (track.id) {
				    track._hasMetadataChanges = true;
				    console.log(`Track "${track.title}" position changed - marked for metadata update`);
				}
			}

            this.prepareUploadTracks = tracks;
		},
		canMoveTrack(evt) {
			if (this.CREATED_ALBUM.beenPublished) {
				return false;
			}
			return true;
		},
		renameTrack(event) {
			const trackElement = event.target;
			const trackIndex = this.prepareUploadTracks.findIndex(track =>
			    track.title === trackElement.value ||
			    trackElement.parentNode.querySelector('.value') === trackElement);

			if (trackIndex !== -1) {
				this.handleMetadataChange(trackIndex); // Use handler here as well
			}
		},

		selectTrackFile() {
			if (this.tracksUploading || this.tracksEdited) { // Add check here
				return;
			}
            if (this.WARNING_CHANGE_STATUS === true) {
				this.$refs.uploadForm.click();
			}
			if (this.CREATED_ALBUM.beenPublished) {
				this.$store.commit('SET_WARNING_CHANGE_STATUS', false);
				this.warningChangeAlbumDialog = true;
			} else {
				this.$refs.uploadForm.click();
			}
		},
		handleTracksUpload(event) {
			const maxLengthTracks = 50;

            if (event.target.files.length > maxLengthTracks) {
				this.uploadLimitDialog = true;
				return;
			}

			for (let track = 0; track < event.target.files.length; track++) {
				let uploadedTracksLength = this.prepareUploadTracks.length;

				if ((this.prepareUploadTracks.length + 1) > maxLengthTracks) {
					this.uploadLimitDialog = true;
					return;
				}

				let fileSize = event.target.files[track].size / 1048576;
				let trackName = event.target.files[track].name;

				let hasTrack = this.prepareUploadTracks.some(el => el.name === trackName || el.trackFileName === trackName);

                if (!hasTrack) {
					if (fileSize < 200) {
						this.releaseInfoChanged = true;
						// Determine artist name - use main artist in single artist mode
						const trackArtistName = this.VARIOUS_ARTISTS ? '' : (this.mainArtistName || '');
						
						// Prepare track artists array with main artist and any secondary artists
						let trackArtists = [];
						
						// Don't add artists in Various Artists mode
						if (!this.VARIOUS_ARTISTS) {
								// Add main artist if available
								if (this.mainArtistName) {
									trackArtists.push({
										id: this.details.artist.id,
										name: this.mainArtistName,
										isAlbumArtist: true,
										isMainArtist: true
									});
								}
							
							// Add secondary artists if this is a Multi-Artist Release
							if (this.savedReleaseStructure === 'Multi-Artist Release' && this.CREATED_ALBUM && 
								this.CREATED_ALBUM.secondaryArtists && this.CREATED_ALBUM.secondaryArtists.length > 0) {
								
								// Add each secondary artist
								this.CREATED_ALBUM.secondaryArtists.forEach(artist => {
									trackArtists.push({
										id: artist.id,
										name: artist.name,
										isAlbumArtist: true,
										isSecondaryArtist: true
									});
								});
								
								console.log(`Added ${this.CREATED_ALBUM.secondaryArtists.length} secondary artists to new track`);
							}
						}
						
						// Create the new track object
						const newTrack = {
							'title': trackName.split(/\.(?=[^\.]+$)/)[0],
							'name': trackName, // Keep original filename
							'trackArtists': trackArtists,
							'key': null,
							'position': uploadedTracksLength + 1, // Position should be 1-based
							'progress': 0,
							'file': event.target.files[track],
							'success': false,
							'bpm': null, // Initialize BPM
							'trackArtist': '' // Initialize with empty string, will be set below
						};
						
						// Add the track to the array
						this.prepareUploadTracks.push(newTrack);
						
						// Get the index of the newly added track
						const newTrackIndex = this.prepareUploadTracks.length - 1;
						
						// Set the trackArtist display string based on the trackArtists array
						if (trackArtists.length > 0) {
							// Generate the display string using our helper method
							this.prepareUploadTracks[newTrackIndex].trackArtist = this.getTrackArtistDisplay(newTrackIndex);
							console.log(`Set track artist display string: ${this.prepareUploadTracks[newTrackIndex].trackArtist}`);
						}
						
						console.log(`Added track: ${trackName} with artist: ${trackArtistName || '(none)'}`);
					} else {
						this.trackLimitDialog = true;
					}
				} else {
                    // This rule applies when we attempt to reload a track in DigitalOcean Spaces.
                    let trackIndex = this.prepareUploadTracks.findIndex(el => el.name === trackName || el.trackFileName === trackName);
                    if (trackIndex !== -1 && !this.prepareUploadTracks[trackIndex].isLosslessUploaded && fileSize < 200) {
                        // Only update file and reset status, DO NOT overwrite existing metadata like artistName
                        this.$set(this.prepareUploadTracks[trackIndex], 'file', event.target.files[track]);
                        this.$set(this.prepareUploadTracks[trackIndex], 'isLosslessUploaded', undefined);
                        this.$set(this.prepareUploadTracks[trackIndex], 'success', false);
                        this.$set(this.prepareUploadTracks[trackIndex], 'progress', 0);
                    }
                }
			}
            this.$refs.uploadForm.value = '';
		},
		confirmDeleteTrack(index) {
			// this.allowRemoveTracksIndex = index;
			// this.warningRemoveTrackDialog = true;

			this.deleteTrack(index);
		},

        trackDeleteProgress(trackId) {
            if (trackId === undefined) {
                return false;
            }

            return this.trackDeleteProgressId === trackId;
        },

		deleteTrack(index) {
            let track = this.prepareUploadTracks[index];
            if (track && track.id) {
                this.trackDeleteProgressId = track.id;

                this.REMOVE_SONG(track.id)
                    .then(() => {
                        this.prepareUploadTracks.splice(index, 1);
                        this.releaseInfoChanged = true;
                        this.$refs.uploadForm.value = '';
                    })
                    .catch(err => console.error(`deleteTrack, ${err}`))
                    .finally(() => this.trackDeleteProgressId = undefined)

                return;
            }

            this.prepareUploadTracks.splice(index, 1);
            this.releaseInfoChanged = true;
            this.$refs.uploadForm.value = '';
			// this.warningRemoveTrackDialog = false;
		},


		// upload cover img album
		changeAva() { // click button "Select"
			// Just reset the error state when changing the image
			this.photoLimitDialog = false;
			this.errorMessage = '';

			// Note: The actual file selection is handled by the CoverImageUploader component
			// The photoUpload method will be called when the file input's change event fires
		},

		clipImage() {
			// This method is now just a proxy to the child component's clipImage
			const clipperComponent = this.$refs.coverImageUploader;
			if (clipperComponent) {
				clipperComponent.onClipImage();
			}
		},

		handleClippedImage(imageFile) {
			// Store the processed image file
			this.uploadedFile = imageFile;

			// Mark as changed
			this.releaseInfoChanged = true;
		},

		photoUpload(event) {
			// Get the file from the event
			if (event && event.target) {
				this.uploadedFile = event.target.files[0];
			}

			if (!this.uploadedFile) {
				console.log('No file selected');
				return;
			}

			// Show loading state
			this.uploadImageLoader = true;

			// Read file and update clipper
			const fileReader = new FileReader();
			fileReader.addEventListener('load', () => {
				this.clipperAvatar = fileReader.result;
				this.clipperDialog = true;

				// Hide loader after a short delay
				setTimeout(() => {
					this.uploadImageLoader = false;
				}, 300);
			});

			fileReader.readAsDataURL(this.uploadedFile);
		},
		imgAvaLoad() {

			const clipperComponent = this.$refs.coverImageUploader;
			const clipper = clipperComponent ? clipperComponent.getClipper() : null;

			if (clipper) {
				clipper.setWH$.next(0.5);
				this.clipperDialog = true;

				// Hide loader after a short delay
				setTimeout(() => {
					this.uploadImageLoader = false;
				}, 300);
			}
		},
		updateDimensions({ width, height }) {
			// Validate dimensions to avoid 0x0 issue
			if (!width || !height || width <= 0 || height <= 0) {
				console.warn('Invalid dimensions received:', width, 'x', height);
				return;
			}

			console.log('Updating dimensions to:', width, 'x', height);
			this.scaleImageWidth = width;
			this.scaleImageHeight = height;

			// Clear any error message since validation is handled by child
			this.errorMessage = '';
		},
		closeClipperDialog() {
			this.clipperDialog = false;
			// Don't reset the clipperAvatar - keep what was already set
		},


		// Album Details
		setGenre(genre) {
			this.details.genre.id = genre.id;
			this.details.genre.name = genre.name;
			this.details.subgenre.id = '';
			this.details.subgenre.name = '';
		},
		setSubgenre(subgenre) {
			this.details.subgenre.id = subgenre.id;
			this.details.subgenre.name = subgenre.name;
		},
		addTag(evt) {
			evt = (evt) ? evt : window.event;
			let charCode = (evt.which) ? evt.which : evt.keyCode;

			this.newTag.trim().normalize('NFC');
			// Enter || Space || Comma || Tab
			if (this.newTag.length && (charCode === 13 || charCode === 32 || charCode === 44 || charCode === 9)) {
				evt.preventDefault();
				this.details.tags.push(this.newTag);
				this.releaseInfoChanged = true;
				this.newTag = '';
			}
		},
		removeTag(ev) {
			if (ev.key === 'Backspace') {
				this.details.tags.splice(-1, 1);
				this.releaseInfoChanged = true;
			}
		},


		changeCurrentStep(step) {
			console.log('Changing step to', step, 'Current label:', this.SELECTED_LABEL);
			
			// Always preserve label and artist before changing steps
			if (this.details.label && this.details.label.id) {
				console.log('Preserving label in store before step change:', this.details.label);
				this.$store.commit('SET_SELECTED_LABEL', {
					id: this.details.label.id,
					title: this.details.label.title
				});
			}
			
			// Persist artist selection as well
			if (this.details.artist && this.details.artist.id) {
				console.log('Preserving artist in store before step change:', this.details.artist);
				this.$store.commit('SET_SELECT_ARTIST', {
					id: this.details.artist.id,
					name: this.details.artist.name
				});
			}
			
			// Preserve release structure if available
			if (this.$refs.releaseInfo && this.$refs.releaseInfo.releaseStructure) {
				console.log('Preserving structure before step change:', this.$refs.releaseInfo.releaseStructure);
				this.savedReleaseStructure = this.$refs.releaseInfo.releaseStructure;
			}
			
			// Handle special case for step 3 to 2 navigation
			if (this.CURRENT_STEP === 3 && step === 2) {
				this.saveCommerce('save', 'dynamic');
			}
			
			this.$store.commit('SET_CURRENT_STEP', step);
			this.createLoading = false;
			
			// Reset change flag when moving to step 2
			if (step === 2) {
				this.releaseInfoChanged = false;
				
				// Ensure track artist names are updated when moving to step 2
				if (this.mainArtistName) {
					// If using various artists mode, only update empty tracks
					// Otherwise, update ALL tracks to match the main artist
					if (this.VARIOUS_ARTISTS) {
						console.log('Various Artists mode - only updating empty track artist names');
						this.updateEmptyTrackArtistNames();
					} else {
						console.log('Single Artist mode - updating ALL track artist names');
						this.updateAllTrackArtistNames();
					}
				}
			}
			
			// Restore structure when returning to step 1
			if (step === 1 && this.savedReleaseStructure) {
				console.log('Restoring structure when returning to step 1:', this.savedReleaseStructure);
				// Use nextTick to ensure the releaseInfo component is mounted
				this.$nextTick(() => {
					if (this.$refs.releaseInfo) {
						// Set the structure
						this.$refs.releaseInfo.releaseStructure = this.savedReleaseStructure;
						
						// Also set the VARIOUS_ARTISTS flag based on the structure
						if (this.savedReleaseStructure === 'Various Artists Release') {
							this.$store.commit('SET_VARIOUS_ARTISTS', true);
							console.log('Restored Various Artists flag to TRUE');
						} else {
							this.$store.commit('SET_VARIOUS_ARTISTS', false);
							console.log('Restored Various Artists flag to FALSE');
						}
					}
				});
			}
		},
		changeAlbumStatus(status) {
			let params = {
				id: this.id,
				status: status,
			}
			this.CHANGE_ALBUM_STATUS(params)
				.then(() => {
					setTimeout(() => {
						// const albumTitle = this.CREATED_ALBUM.title.trim().replace(/ /g, '-');
						this.$router.push({name: 'albumDetails', params: {id: this.id, name: window.toHumanFormat(this.CREATED_ALBUM.identifier)}});
					}, 600);
				})
				.catch(err => console.error(`changeAlbumStatus, ${err}`))
		},


		async storeTrack(track, index, isReUpload) {
			this.tracksUploading = true;

			let instance = axios.create();
			delete instance.defaults.headers.common['Authorization'];

            let trackUrl = this.PREPARE_TRACK.url ?? this.prepareUploadTracks[index].preSignedUploadUrl;

            // Return a Promise so we can properly chain track uploads
            return new Promise((resolve, reject) => {
                instance.put(trackUrl, track, {
                    onUploadProgress: (progressEvent) => {
                        this.prepareUploadTracks[index].progress = parseInt(Math.round(progressEvent.loaded * 100 / progressEvent.total));
                        this.$set(this.prepareUploadTracks, index, this.prepareUploadTracks[index])
                    }
                })
                .then(() => {
                    this.prepareUploadTracks[index].success = true;

                    // After successfully uploading a track to DigitalOcean, we need to notify the backend and set the isLosslessUploaded flag
                    let songId = this.prepareUploadTracks[index].id;
                    if (songId) {
                        this.EDIT_SONG({
                            id: songId,
                            isLosslessUploaded: true
                        }).then(() => {
                            if (this.prepareUploadTracks[index]) {
                                this.prepareUploadTracks[index].isLosslessUploaded = true;
                            }
                            resolve(); // Resolve the Promise after everything is done
                        }).catch(error => {
                            reject(error); // Reject if EDIT_SONG fails
                        });
                    } else {
                        resolve(); // Resolve if no song ID to update
                    }
                })
                .catch(err => {
                    this.prepareUploadTracks[index].success = false;

                    this.$emit('retryUploadTrack', {
                        'trackFile': track,
                        'trackIndex': index,
                        'isReUpload': isReUpload ?? false
                    });

                    if (err.toString().includes('Network Error') && isReUpload) {
                        this.isConnected = false;
                    }

                    reject(err); // Reject the Promise on error
                });
                // Remove automatic step transitions - now handled by processTracksSequentially
            });
		},
		async storeCoverImage(imageFormData) {
			console.log('imageFormData', imageFormData);
			this.changeAvatarLoader = true;
			await this.ADD_ALBUM_IMAGE(imageFormData)
				.then(() => {
					console.log('then storeCoverImage - imageFormData', imageFormData);
				})
				.catch(err => {
					console.error(`storeCoverImage, ${err}`);
				})
				.finally(() => {
					this.changeAvatarLoader = false;

					if (this.ALBUM_IMAGE_STATUS && this.ALBUM_IMAGE_STATUS === 'success') {
						if (this.tracksUploading === false && this.tracksEdited === false && this.tracksDeleted === false && this.changeAvatarLoader === false) {
							this.changeCurrentStep(3);
						}
					} else {
						this.createLoading = false;
						this.validImage = false;
					}
				})
		},


		saveReleaseInfo(save) {
			console.log('saveReleaseInfo', save);
			
			// Ensure label from store is synchronized with component state
			if (this.SELECTED_LABEL && this.SELECTED_LABEL.id) {
				console.log('Syncing label from store before save:', this.SELECTED_LABEL);
				this.details.label.id = this.SELECTED_LABEL.id;
				this.details.label.title = this.SELECTED_LABEL.title;
			}
			
			if (save === 'continue') {
				this.$v.details.title.$touch();
				this.$v.details.genre.id.$touch();
				this.$v.details.subgenre.id.$touch();
				this.$v.datePicker.$touch();
				if (!this.VARIOUS_ARTISTS) {
					this.$v.details.artist.name.$touch();
				}

			}
			this.$v.details.title.$touch();

			if ((save === 'continue' &&
					this.$v.details.title.$error === false &&
					this.$v.details.genre.id.$error === false &&
					this.$v.details.subgenre.id.$error === false &&
					this.$v.datePicker.$error === false &&
					this.$v.details.artist.name.$error === false) || (save === 'save' && this.$v.details.title.$error === false) || save === 'publish' && this.$v.details.title.$error === false) {

				let artistID = this.details.artist.id;
				let selectedArtist = this.YOUR_ARTISTS.find(el => el.id === artistID);

				if (!this.VARIOUS_ARTISTS) {
					if (
						!selectedArtist || !selectedArtist.hasActiveSubscription || (!selectedArtist.hasActiveSubscription && !selectedArtist.subscription)
						|| (!selectedArtist.hasActiveSubscription && selectedArtist.subscription && !selectedArtist.subscription.canActivate)
					) {
						this.artistSubscriptionError = true;
						return;
					}
				}


				if (!this.releaseInfoChanged) {
					this.createLoading = true;
					this.changeCurrentStep(2);
					return;
				}

				const albumDetails = {
					title: this.details.title.normalize('NFC'),
					artist: !this.details.artist.id ? null : `artists/${this.details.artist.id}`,
					recordLabel: !this.details.label.id ? null : `record_labels/${this.details.label.id}`,
					description: !this.details.description ? null : this.details.description.normalize('NFC'),
					type: 'album',
					tags: !this.details.tags ? null : this.details.tags,
					genre: !this.details.genre.id ? null : `genres/${this.details.genre.id}`,
					subGenres: !this.details.subgenre.id ? [] : [`sub_genres/${this.details.subgenre.id}`],
					createdAt: new Date(this.datePicker).toISOString(),
					releasedAt: new Date(this.datePicker).toISOString(),
					variousArtists: this.VARIOUS_ARTISTS ? 1 : 0,
				};

				this.createLoading = true;


				if (!this.id) {
					this.ADD_MUSIC_RELEASE(albumDetails)
						.then(() => {
							if (this.TEMP_CREATED_ALBUMS_ID) {
								this.$router.replace({name: 'createMusicRelease', params: {id: this.TEMP_CREATED_ALBUMS_ID}});
								this.changeCurrentStep(2);
								this.releaseInfoChanged = false;
							}
						})
						.finally(() => {
							this.createLoading = false;
						})
				} else {
					albumDetails.id = +this.id;
					this.EDIT_MUSIC_RELEASE(albumDetails)
						.then(() => {
							if (this.TEMP_CREATED_ALBUMS_ID) {
								this.changeCurrentStep(2);
								this.releaseInfoChanged = false;
							}
						})
						.finally(() => {
							this.createLoading = false;
						});
				}
			}
		},
		saveFiles(save) {
            setTimeout(() => {
                let losslessUploadedFailed = this.prepareUploadTracks.filter(track => track.isLosslessUploaded === false);
                if (losslessUploadedFailed.length) {
                    this.losslessUploadedFailedDialog = true;
                    return;
                }

                if (save === 'continue') {
                    this.$v.prepareUploadTracks.$touch();
                    this.$v.clipperAvatar.$touch();
                    
                    // Check validation state
                    if (this.$v.prepareUploadTracks.$error) {
                        this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', 'Please fill in all required fields');
                        this.createLoading = false;
                        return;
                    }
                }
                
                if (this.id) {
                    if ((save === 'continue' && this.$v.prepareUploadTracks.$error === false && this.$v.clipperAvatar.$error === false) || save === 'save' || save === 'publish') {
                        this.createLoading = true;

                        // Reset processing state
                        this._tracksProcessingComplete = false;
                        this._imageProcessingComplete = false;
                        this._totalTracksToProcess = 0;

                        // If release info has changed (potentially order changes), mark tracks that need metadata updates
                        if (this.releaseInfoChanged) {
                            console.log("Release info has changed, checking tracks for metadata updates");
                            // Flag tracks with position or title changes
                            this.prepareUploadTracks.forEach((track, idx) => {
                                if (track.id) {
                                    // For existing tracks, mark them for metadata update
                                    track._hasMetadataChanges = true;
                                    console.log(`Marking track "${track.title}" for metadata update due to release changes`);
                                }
                            });
                        }

                        // Track both track and image processing completion
                        this._tracksProcessingComplete = !this.prepareUploadTracks.length;
                        this._imageProcessingComplete = !this.uploadedFile;

                        // Check if we can immediately proceed (no tracks or images to process)
                        if (this._tracksProcessingComplete && this._imageProcessingComplete && save === 'continue') {
                            this.updateAlbumData();
                            this.changeCurrentStep(3);
                            return;
                        }

                        // Process tracks sequentially to avoid database deadlocks
                        if (this.prepareUploadTracks.length) {
                            this.processTracksSequentially(0, save);
                        }

                        // Cover image processing
                        if (this.uploadedFile) {
                            let imageFormData = new FormData();
                            imageFormData.append('file', this.uploadedFile);
                            imageFormData.id = this.id;

                            this.changeAvatarLoader = true;
                            this.ADD_ALBUM_IMAGE(imageFormData)
                                .then(() => {
                                    console.log("Cover image uploaded successfully");
                                })
                                .catch(err => {
                                    console.error(`storeCoverImage, ${err}`);
                                })
                                .finally(() => {
                                    this.changeAvatarLoader = false;
                                    this._imageProcessingComplete = true;

                                    // Check if we can proceed to step 3 (if tracks are also done)
                                    if (this._tracksProcessingComplete && save === 'continue') {
                                        this.updateAlbumData();
                                        this.changeCurrentStep(3);
                                    }
                                });
                        }
                    }
                } else {
                    this.changeCurrentStep(1);
                }
            }, 500);
        },

        // Process tracks one by one to avoid concurrent database updates
        processTracksSequentially(index, save) {
            // Store the total number of tracks at the start of processing to prevent issues if array changes
            if (index === 0) {
                // Count tracks that need processing (new, modified or metadata changes)
                this._totalTracksToProcess = this.prepareUploadTracks.length;
                this._tracksToProcess = this.prepareUploadTracks.filter(track => {
                    // Log reason for processing each track
                    if (!track.id) {
                        console.log(`Track "${track.title}" needs processing: New track`);
                        return true;
                    } else if (track.file && undefined === track.isLosslessUploaded) {
                        console.log(`Track "${track.title}" needs processing: Pending upload`);
                        return true;
                    } else if (track.isLosslessUploaded === false) {
                        console.log(`Track "${track.title}" needs processing: Failed upload that needs retry`);
                        return true;
                    } else if (track._hasMetadataChanges === true) {
                        console.log(`Track "${track.title}" needs processing: Has metadata changes`);
                        return true;
                    }
                    return false;
                }).length;

                console.log(`Starting to process ${this._tracksToProcess} tracks out of ${this._totalTracksToProcess} total`);

                // If no tracks need processing, mark as complete immediately
                if (this._tracksToProcess === 0) {
                    console.log("No tracks need processing, marking as complete");
                    this.tracksUploading = false;
                    this.tracksEdited = false;
                    this._tracksProcessingComplete = true;

                    // If image processing is also complete and we're in "continue" mode, proceed to step 3
                    if (this._imageProcessingComplete && save === 'continue') {
                        this.updateAlbumData();
                        this.changeCurrentStep(3);
                    } else if (save !== 'continue') {
                        // Just remove the loading state for save/publish mode
                        this.createLoading = false;
                    }
                    return;
                }
            }

            // Check if we've processed all tracks
            if (index >= this._totalTracksToProcess) {
                // All tracks processed - transition to the next step
                console.log("All tracks processed successfully");
                this.tracksUploading = false;
                this.tracksEdited = false;
                this._tracksProcessingComplete = true;

                // If image processing is also complete and we're in "continue" mode, proceed to step 3
                if (this._imageProcessingComplete && save === 'continue') {
                    this.createLoading = false;
                    this.updateAlbumData();
                    this.changeCurrentStep(3);
                } else if (save !== 'continue') {
                    // Just remove the loading state for save/publish mode
                    this.createLoading = false;
                }
                return;
            }

            const track = this.prepareUploadTracks[index];

            // Skip already uploaded tracks (unless they have changes that need processing)
            if (track.id && track.isLosslessUploaded === true && !track.file && !track._hasMetadataChanges) {
                console.log(`Skipping already uploaded track ${index+1}/${this._totalTracksToProcess}: ${track.title}`);
                // Skip to next track
                this.processTracksSequentially(index + 1, save);
                return;
            }

            console.log(`Processing track ${index+1}/${this._totalTracksToProcess}: ${track.title}`);
            // Process new tracks
            if (!track.id) {
                this.tracksUploading = true;

                // For ADD_SONGS, pass the key ID directly (not as an IRI)
                let keyValue = !track.key ? null : (typeof track.key === 'object' && track.key.id) ? track.key.id : null;

                let song = {
                    id: +this.id,
                    trackName: track.title.normalize('NFC'),
                    // Always use trackArtist for track-level artists
                    trackArtist: track.trackArtist.normalize('NFC') || null,
                    trackFileName: track.name.normalize('NFC'),
                    trackFileExtension: track.name.split(/\.(?=[^\.]+$)/)[1],
                    bpm: !track.bpm ? 0 : Number(track.bpm),
                    key: keyValue,
                    style: !this.subgenre ? [] : [this.subgenre],
                    position: track.position,
                }

                this.ADD_SONGS(song)
                    .then(() => {
                        this.prepareUploadTracks[index].id = this.PREPARE_TRACK.id;
                        // Upload track file then process next track when complete
                        this.storeTrack(track.file, index)
                            .then(() => {
                                // Process next track
                                this.processTracksSequentially(index + 1, save);
                            })
                            .catch(err => {
                                console.error(`Failed to upload track at index ${index}:`, err);
                                // Continue with next track even if this one failed
                                this.processTracksSequentially(index + 1, save);
                            });
                    })
                    .catch(err => {
                        console.error(`ADD_SONGS, ${err}`);
                        // Try next track even if this one failed
                        this.processTracksSequentially(index + 1, save);
                    });
            }
            // Process tracks with metadata changes only (no file upload needed)
            else if (track.id && track._hasMetadataChanges && (!track.file || track.isLosslessUploaded === true)) {
                console.log(`Processing metadata changes for track: ${track.title}, position: ${track.position}`);
                this.tracksEdited = true;
                track.updateSuccess = false;

                // Always provide all metadata to both endpoints to ensure everything gets updated
                let song = {
                    id: track.id,
                    releaseId: this.id,
                    title: track.title.normalize('NFC'),
                    trackArtist: track.trackArtist.normalize('NFC') || null, // Include trackArtist
                    bpm: !track.bpm ? 0 : Number(track.bpm),
                    key: !track.key ? null : (typeof track.key === 'object' && track.key.id) ? `song_keys/${track.key.id}` : null,
                    position: track.position,
                }

                // Create a combined update that uses both endpoints to ensure both name and position get updated
                console.log(`Updating track metadata with combined approach`, JSON.stringify(song));

                // First update the position using UPDATE_SONG
                this.UPDATE_SONG(song)
                    .then(() => {
                        console.log(`Position update successful for track: ${track.title}`);

                        // Then update the title using EDIT_SONG
                        return this.EDIT_SONG({
                            id: track.id,
                            title: track.title.normalize('NFC'),
                            trackArtist: track.trackArtist.normalize('NFC') || null, // Include trackArtist
                            bpm: !track.bpm ? 0 : Number(track.bpm),
                            key: !track.key ? null : (typeof track.key === 'object' && track.key.id) ? `song_keys/${track.key.id}` : null,
                        });
                    })
                    .then(() => {
                        console.log(`Title update successful for track: ${track.title}`);
                        track.updateSuccess = true;
                        track._hasMetadataChanges = false;
                        // Process next track
                        this.processTracksSequentially(index + 1, save);
                    })
                    .catch(err => {
                        console.error(`Failed to update track metadata: ${err}`);
                        // Try next track even if this one failed
                        this.processTracksSequentially(index + 1, save);
                    });
            }
            // Process existing tracks with file uploads
            else {
                this.tracksEdited = true;
                track.updateSuccess = false;

                // Always provide all metadata to both endpoints to ensure everything gets updated
                let song = {
                    id: track.id,
                    releaseId: this.id,
                    title: track.title.normalize('NFC'),
                    trackArtist: track.trackArtist.normalize('NFC') || null, // Include trackArtist
                    bpm: !track.bpm ? 0 : Number(track.bpm),
                    key: !track.key ? null : (typeof track.key === 'object' && track.key.id) ? `song_keys/${track.key.id}` : null,
                    position: track.position,
                }

                console.log(`Updating track metadata with combined approach`, JSON.stringify(song));

                // First update position using UPDATE_SONG
                this.UPDATE_SONG(song)
                    .then(() => {
                        console.log(`Position update successful for track: ${track.title}`);

                        // Then update title using EDIT_SONG
                        return this.EDIT_SONG({
                            id: track.id,
                            title: track.title.normalize('NFC'),
                            trackArtist: track.trackArtist.normalize('NFC') || null, // Include trackArtist
                            bpm: !track.bpm ? 0 : Number(track.bpm),
                            key: !track.key ? null : (typeof track.key === 'object' && track.key.id) ? `song_keys/${track.key.id}` : null,
                        });
                    })
                    .then(() => {
                        console.log(`Title update successful for track: ${track.title}`);

                        // Now handle file upload if needed
                        if (undefined === track.isLosslessUploaded && track.file) {
                            // Upload track file then process next track when complete
                            return this.storeTrack(track.file, index)
                                .then(() => {
                                    track.updateSuccess = true;
                                    // Reset metadata change flag if it exists
                                    if (track._hasMetadataChanges) {
                                        track._hasMetadataChanges = false;
                                    }
                                });
                        } else {
                            track.updateSuccess = true;
                            // Reset metadata change flag if it exists
                            if (track._hasMetadataChanges) {
                                track._hasMetadataChanges = false;
                            }
                            return Promise.resolve();
                        }
                    })
                    .then(() => {
                        // Process next track
                        this.processTracksSequentially(index + 1, save);
                    })
                    .catch(err => {
                        console.error(`Failed to update track: ${err}`);
                        // Try next track even if this one failed
                        this.processTracksSequentially(index + 1, save);
                    });
            }
        },


		// commerce stripe
		getStripeData() {
			this.GET_STRIPE_ACCOUNT()
				.then(() => {
					if (this.stripeStatus !== 'no-account') {
            let params = {
              callback_success_url: `${window.location.protocol}//${window.location.host}/create-music-release/${this.id}#step=1`,
            }
            this.GET_LINK_STRIPE_ACCOUNT(params)
							.catch(err => {
								console.error(`getStripeData, ${err}`);
							})
					}
				})
				.catch(err => {
					console.error(`getStripeAccount, ${err}`);
				})
		},
		createStripe() {
			let params = {
				callback_success_url: `${window.location.protocol}//${window.location.host}/create-music-release/${this.id}#step=3`,
			}
			this.CREATE_STRIPE_ACCOUNT(params)
				.then(() => {
          window.location.href = this.CREATE_LINK_STRIPE_ACCOUNT;
					// window.open(this.CREATE_LINK_STRIPE_ACCOUNT, '_blank');
				})
				.catch(err => {
					console.error(`createStripe,npm ${err}`);
				})
				.finally(() => {
					this.getStripeData();
				})
		},
		resolveStripeIssue() {
			let params = {
				callback_success_url: `${window.location.protocol}//${window.location.host}/create-music-release/${this.id}#step=3`,
			}
			this.GET_LINK_STRIPE_ACCOUNT(params)
				.then(() => {
					window.location.href = this.STRIPE_ACCOUNT_LINK;
				})
				.catch(err => {
					console.error(`getLinkStripeAccount, ${err}`);
				})
		},
		visitStripe() {
			window.location.href = this.STRIPE_ACCOUNT_LINK;
		},

		onBlurTrackPrice(value, index) {
			if (value.price !== null) {
				let newValue = String(value.price);
				if (newValue.length !== 0) {
					if (newValue.indexOf(".") === -1) {
						newValue = newValue + ".00";
					} else if (newValue.length - newValue.indexOf(".") - 1 < 2) {
						newValue = newValue + "0".repeat(2 - (newValue.length - newValue.indexOf(".") - 1));
					}
					this.MUSIC_RELEASE_COMMERCE.songs[index].price = parseFloat(newValue).toFixed(2);
				}
			}
		},
		albumPriceLimit(event) {
			const inputChar = event.key;
			if (inputChar !== '.' && isNaN(inputChar)) {
				event.preventDefault();
			} else {
				if (this.albumPrice > 1000) {
					this.albumPriceLimitDialog = true;
				}
			}
		},
		trackPriceLimit(event, index) {
			const inputChar = event.key;
			if (inputChar !== '.' && isNaN(inputChar)) {
				event.preventDefault();
			} else {
				if (this.MUSIC_RELEASE_COMMERCE.songs[index].price > 100) {
					this.trackPriceLimitDialog = true;
				}
			}
		},
		setToZero() {
			this.albumPrice = '0.00';
			this.distributionAlbumPrice('zero');
		},
		distributionAlbumPrice(variantDistribution) {
			let songs = this.MUSIC_RELEASE_COMMERCE.songs;
			let songsLength = songs.length;
			for (let i = 0; i < songsLength; i++) {
				let price = this.albumPrice / songsLength;

				switch (variantDistribution) {
					// case 'floor':
					// 	songs[i].price = Math.floor(price).toFixed(2);
					// 	break;
					case 'ceil':
						songs[i].price = Math.ceil(price).toFixed(2);
						break;
					case 'zero':
						songs[i].price = '0.00';
						break;
					case null:
						songs[i].price = null;
						break;
				}
			}
			if (this.priceDistributionDialog) {
				this.priceDistributionDialog = false;
			}
			if (this.userDecidesPriceDialog) {
				this.userDecidesPriceDialog = false;
			}
		},
		changeAllowTracksChecked() {
			if (this.CURRENT_STEP === 3 && this.allowTracksChecked === true && this.albumPrice > 0) {
				this.priceDistributionDialog = true;
			}
		},
		async saveCommerce(save, afterSave) {
			this.validCommence = true;

			// check publish free
			if (this.publishFree === true) {
				this.albumPrice = null;
				await this.distributionAlbumPrice('null');
			}

			if (this.allowTracksChecked === false) {
				await this.distributionAlbumPrice('null');
			}

			// show userDecidesPriceDialog
			if (save === 'continue' && this.publishFree === false && !this.albumPrice) {
				this.userDecidesPriceDialog = true;
				this.validCommence = false;
				return;
			}

			// check empty tracks price
			let checkEmptyTracksPrice = this.MUSIC_RELEASE_COMMERCE.songs.find(track => (!track.price || false));
			if (save === 'continue' && this.publishFree === false && (+this.albumPrice === 0 || +this.albumPrice > 0) && (this.allowTracksChecked && checkEmptyTracksPrice)) {
				this.priceDistributionDialog = true;
				this.validCommence = false;
				return;
			}


			// check max price
			let checkValidTracksPrice = this.MUSIC_RELEASE_COMMERCE.songs.find(track => track.price > 100);
			if (this.publishFree !== true && (!this.albumPrice || this.albumPrice > 1000 || (this.allowTracksChecked && checkValidTracksPrice))) {
				this.validCommence = false;
				return;
			}


			if (this.validCommence === true) {
				this.createLoading = true;

				let params = {
					id: this.id,
					is_free: this.publishFree,
					price: this.publishFree !== true ? +this.albumPrice : null
				}
				if (this.MUSIC_RELEASE_COMMERCE.songs.length) {
					let songs = [];
					for (let track of this.MUSIC_RELEASE_COMMERCE.songs) {

						let tracksPrice;
						if (this.publishFree || this.allowTracksChecked === false) {
							tracksPrice = null;
						}
						if (this.allowTracksChecked === true && !this.publishFree) {
							tracksPrice = +track.price;
						}
						// if (this.allowTracksChecked === false && !this.publishFree) {
						// 	tracksPrice = 0;
						// }

						songs.push({
							id: track.id,
							price: tracksPrice
						});
					}
					params.songs = songs;
				}

				this.POST_MUSIC_RELEASE_COMMERCE(params)
					.then(() => {
						if (save === 'continue') {
							setTimeout(() => {
								// console.log('this.CREATED_ALBUM', this.CREATED_ALBUM);
								// const albumTitle = this.CREATED_ALBUM.title.trim().replace(/ /g, '-');
								this.$router.push({name: 'albumDetails', params: {id: this.id, name: window.toHumanFormat(this.CREATED_ALBUM.identifier)}});
								this.createLoading = false;
							}, 600);
						}
						if (afterSave === 'dynamic') {
							this.createLoading = false;
						}
						if (save === 'publish' && afterSave === 'default') {
							this.changeAlbumStatus('pending');
						}
					})
					.catch(err => console.error(`POST_MUSIC_RELEASE_COMMERCE, ${err}`))
			}
		},
        startTimer() {
            this.showTimer = true;
            this.secondsRemaining = 15;
            this.intervalId = setInterval(() => {
                this.secondsRemaining > 0 ? this.secondsRemaining-- : this.stopTimer();
            }, 1000);
        },
        stopTimer() {
            clearInterval(this.intervalId);

            this.checkConnection((status) => {
                this.isConnected = status;

                if (!this.isConnected) {
                    this.startTimer();
                }
            })
        },

        checkConnection(callback) {
            let xhr = new (window.ActiveXObject || XMLHttpRequest)("Microsoft.XMLHTTP");

            xhr.onreadystatechange = function() {
                if (xhr.readyState == 4) {
                    if ((xhr.status >= 200 && xhr.status < 300) || xhr.status == 304) {
                        callback(true);
                    } else {
                        callback(false);
                    }
                }
            };

            try {
                xhr.open("HEAD", process.env.VUE_APP_API_URL, true);
                xhr.send();
            } catch (error) {
                callback(false);
            }
        },

		// Handlers for CommerceSection events
		handleSaveCommerceComplete({ save, afterSave }) {
			if (save === 'continue') {
				setTimeout(() => {
					this.$router.push({
						name: 'albumDetails',
						params: {id: this.id, name: window.toHumanFormat(this.CREATED_ALBUM.identifier)}
					});
					this.createLoading = false;
				}, 600);
			}
			if (afterSave === 'dynamic') {
				this.createLoading = false;
			}
			if (save === 'publish' && afterSave === 'default') {
				this.changeAlbumStatus('pending');
			}
		},

		handleSaveCommerceError(err) {
			console.error(`Failed to save commerce settings: ${err}`);
			this.createLoading = false;
		},

		// Handler for ReleaseInfo initialization
		onReleaseInfoInitialized(releaseData) {
			console.log('ReleaseInfo component initialized with data:', releaseData);
			
			// Set the main artist name if available
			if (releaseData.details && releaseData.details.artist && releaseData.details.artist.name) {
				this.mainArtistName = releaseData.details.artist.name;
				console.log(`Main artist name set from ReleaseInfo initialization: ${this.mainArtistName}`);
				
				// Update track artist names based on current mode
				if (!this.VARIOUS_ARTISTS) {
					this.updateAllTrackArtistNames();
				} else {
					this.updateEmptyTrackArtistNames();
				}
			}
		},
		// New method to handle metadata changes
		handleMetadataChange(index) {
			if (index >= 0 && index < this.prepareUploadTracks.length) {
				const track = this.prepareUploadTracks[index];
				
				// Clear any error messages about missing artist names in Various Artists mode
				if (this.VARIOUS_ARTISTS && track.trackArtist && 
					this.RELEASE_INFO_ERROR_MESSAGE && 
					(this.RELEASE_INFO_ERROR_MESSAGE.includes('Various Artists mode') || 
					 this.RELEASE_INFO_ERROR_MESSAGE.includes('Track artist is required'))) {
					this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', '');
				}
				
				// Only mark as changed if it has an ID (existing track) and actually has changes
				if (track.id) {
					// Check against original data if available, otherwise assume change
					// For simplicity now, we just mark it changed if it has an ID
					track._hasMetadataChanges = true;
					this.releaseInfoChanged = true;
					console.log(`Track "${track.title}" (index ${index}) marked for metadata update`);
				}
			} else {
				console.warn(`handleMetadataChange called with invalid index: ${index}`);
			}
		},

        // New method to update ALL track artist names
        updateAllTrackArtistNames() {
            if (!this.mainArtistName) return;

            console.log(`Updating ALL tracks to include album artist: ${this.mainArtistName}`);
            
            // Current album artist
            const albumArtist = {
              id: this.details.artist.id,
              name: this.mainArtistName,
              isAlbumArtist: true
            };
            
            // Update artists for ALL tracks
            this.prepareUploadTracks.forEach((track, index) => {
              // Handle both old and new format
              if (!track.trackArtists) {
                // Convert from old format
                track.trackArtists = [];
                
                // If track has an existing artist and it's different from album artist,
                // add it as a non-album artist
                if (track.trackArtist && track.trackArtist !== this.mainArtistName) {
                  track.trackArtists.push({
                    id: track.trackArtistId || '',
                    name: track.trackArtist,
                    isAlbumArtist: false
                  });
                }
              }
              
              // Remove any existing album artists
              let nonAlbumArtists = track.trackArtists.filter(artist => !artist.isAlbumArtist);
              
              // Also remove any artists that match the new album artist ID
              if (this.details.artist.id) {
                nonAlbumArtists = nonAlbumArtists.filter(artist => artist.id !== this.details.artist.id);
              }
              
              // Add the new album artist
              track.trackArtists = [albumArtist, ...nonAlbumArtists];
              
              // Update the display string
              track.trackArtist = this.getTrackArtistDisplay(index);
              
              // Mark as changed if it's an existing track
              if (track.id) {
                track._hasMetadataChanges = true;
                this.releaseInfoChanged = true;
              }
            });
        },

        // New method to update empty track artist names
        updateEmptyTrackArtistNames() {
            if (!this.mainArtistName) return;

            // Update artist name for any tracks that don't have one set
            this.prepareUploadTracks.forEach((track, index) => {
                if (!track.trackArtist) {
                    console.log(`Setting artist name for track ${index+1}: ${track.title}`);
                    track.trackArtist = this.mainArtistName;

                    // Mark as changed if it's an existing track
                    if (track.id) {
                        track._hasMetadataChanges = true;
                        this.releaseInfoChanged = true;
                    }
                }
            });
        },

        // New method to open artist dialog for a specific track
        openArtistDialogForTrack(index) {
            // Prevent opening if readonly or upload/edit in progress
            if (this.tracksUploading || this.tracksEdited || (this.CREATED_ALBUM && this.CREATED_ALBUM.beenPublished)) {
                return;
            }
            
            // Clear any previous error messages about missing artist names
            if (this.VARIOUS_ARTISTS && this.RELEASE_INFO_ERROR_MESSAGE && 
                (this.RELEASE_INFO_ERROR_MESSAGE.includes('Various Artists mode') || 
                 this.RELEASE_INFO_ERROR_MESSAGE.includes('Track artist is required'))) {
                this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', '');
            }
            
            // In Various Artists mode, use our specialized component
            if (this.VARIOUS_ARTISTS) {
                console.log(`Opening specialized track artist selector for track index: ${index}`);
                this.trackArtistSelectorIndex = index;
                this.trackArtistSelectorVisible = true;
            } 
            // In single artist mode, always open the track artist selector dialog
            else if (this.mainArtistName && this.details.artist.id) {
                console.log(`Opening specialized track artist selector for track index: ${index} (with album artist)`);
                this.trackArtistSelectorIndex = index;
                this.trackArtistSelectorVisible = true;
            }
            // If no main artist selected yet, open the original dialog
            else {
                this.editingTrackArtistIndex = index;
                this.viewListAccounts = 'Artist';
                this.$store.commit('SET_SELECT_ARTIST', null);
                this.modalCreateNewAccount = true;
            }
        },

        // Add a handler for the artist-selected event
        handleTrackArtistSelected(artistData) {
            console.log(`Artist selected for track ${artistData.trackIndex}:`, artistData.name);
            
            // Update the track's artist
            if (artistData.trackIndex >= 0 && artistData.trackIndex < this.prepareUploadTracks.length) {
                this.$set(this.prepareUploadTracks[artistData.trackIndex], 'trackArtist', artistData.name);
                this.handleMetadataChange(artistData.trackIndex);
            }
            
            // Reset the index
            this.trackArtistSelectorIndex = null;
        },

        // Add a handler for when label is cleared from the child component
        handleLabelCleared() {
            console.log('Label was explicitly cleared by user in ReleaseInfo component');
            this.labelExplicitlyCleared = true;
            // Also ensure the store reflects this
            this.$store.commit('SET_SELECTED_LABEL', null);
        },
        showTrackArtistError(index) {
            return this.$v.prepareUploadTracks.$dirty && 
                   this.$v.prepareUploadTracks.$each && 
                   this.$v.prepareUploadTracks.$each[index] && 
                   this.$v.prepareUploadTracks.$each[index].trackArtist.$error;
        },

        // Add helper methods
        getAlbumArtistsForTrack() {
          // If in Various Artists mode or no main artist, return empty array
          if (this.VARIOUS_ARTISTS || !this.mainArtistName) {
            return [];
          }
          
          // Start with the main artist
          // Find the main artist in YOUR_ARTISTS to get the avatar
          const mainArtist = this.YOUR_ARTISTS.find(artist => artist.id === this.details.artist.id);
          
          const albumArtists = [{
            id: this.details.artist.id,
            name: this.mainArtistName,
            isAlbumArtist: true,
            isMainArtist: true,
            avatar: mainArtist ? mainArtist.avatar : null // Include avatar data
          }];
          
          // Add secondary artists if this is a Multi-Artist Release
          if (this.savedReleaseStructure === 'Multi-Artist Release' && this.CREATED_ALBUM && 
              this.CREATED_ALBUM.secondaryArtists && this.CREATED_ALBUM.secondaryArtists.length > 0) {
            
            // Add each secondary artist as an album artist
            this.CREATED_ALBUM.secondaryArtists.forEach(secondaryArtist => {
              // Find the artist in YOUR_ARTISTS to get the avatar
              const artistWithAvatar = this.YOUR_ARTISTS.find(artist => artist.id === secondaryArtist.id);
              
              albumArtists.push({
                id: secondaryArtist.id,
                name: secondaryArtist.name,
                isAlbumArtist: true,  // Mark as album artist
                isSecondaryArtist: true,
                avatar: artistWithAvatar ? artistWithAvatar.avatar : null // Include avatar data
              });
            });
            
            console.log(`Including ${this.CREATED_ALBUM.secondaryArtists.length} secondary artists in album artists list`);
          }
          
          return albumArtists;
        },

        getTrackArtists(index) {
          if (index < 0 || index >= this.prepareUploadTracks.length) {
            return [];
          }
          
          const track = this.prepareUploadTracks[index];
          
          // Handle both old and new format
          if (track.trackArtists && Array.isArray(track.trackArtists)) {
            // Return only non-album artists and artists that don't match the album artist
            return track.trackArtists.filter(artist => {
              // Filter out album artists
              if (artist.isAlbumArtist) return false;
              
              // Also filter out artists that match the album artist ID
              if (this.details.artist.id && artist.id === this.details.artist.id) return false;
              
              // Keep all other artists
              return true;
            });
          } else if (track.trackArtist) {
            // Legacy support: convert string to array if needed
            // Return empty array if the track artist matches the album artist
            return this.mainArtistName && track.trackArtist === this.mainArtistName ? 
              [] : 
              [{
                id: track.trackArtistId || '',
                name: track.trackArtist
              }];
          }
          
          return [];
        },

        // Return a formatted display string for track artists
        getTrackArtistDisplay(index) {
          if (index < 0 || index >= this.prepareUploadTracks.length) {
            return '';
          }
          
          const track = this.prepareUploadTracks[index];
          
          // Handle both old and new format
          if (track.trackArtists && Array.isArray(track.trackArtists) && track.trackArtists.length > 0) {
            // Get album artists and other artists
            const albumArtists = track.trackArtists.filter(artist => artist.isAlbumArtist);
            const otherArtists = track.trackArtists.filter(artist => !artist.isAlbumArtist);
            
            // Format based on what we have
            if (albumArtists.length > 0 && otherArtists.length > 0) {
              return `${albumArtists.map(a => a.name).join(', ')} feat. ${otherArtists.map(a => a.name).join(', ')}`;
            } else {
              return track.trackArtists.map(a => a.name).join(', ');
            }
          } else if (track.trackArtist) {
            // Legacy support
            return track.trackArtist;
          }
          
          return '';
        },

        // Replace handleTrackArtistSelected with the new version
        handleTrackArtistsSelected(artistsData) {
          console.log(`Artists selected for track ${artistsData.trackIndex}:`, artistsData.artists);
          
          if (artistsData.trackIndex >= 0 && artistsData.trackIndex < this.prepareUploadTracks.length) {
            // Update the track's artists array
            this.$set(this.prepareUploadTracks[artistsData.trackIndex], 'trackArtists', artistsData.artists);
            
            // For backward compatibility, also update the trackArtist string
            if (artistsData.artists.length > 0) {
              // Format the display string
              const displayString = this.getTrackArtistDisplay(artistsData.trackIndex);
              this.$set(this.prepareUploadTracks[artistsData.trackIndex], 'trackArtist', displayString);
            } else {
              this.$set(this.prepareUploadTracks[artistsData.trackIndex], 'trackArtist', '');
            }
            
            this.handleMetadataChange(artistsData.trackIndex);
          }
          
          // Reset the index
          this.trackArtistSelectorIndex = null;
        },

        // Update methods for handling album artist changes
        updateAllTrackArtistNames() {
          if (!this.mainArtistName) return;

          console.log(`Updating ALL tracks to include album artist: ${this.mainArtistName}`);
          
          // Current album artist
          const albumArtist = {
            id: this.details.artist.id,
            name: this.mainArtistName,
            isAlbumArtist: true
          };
          
          // Get secondary artists if this is a Multi-Artist Release
          let secondaryArtists = [];
          if (this.savedStructure === 'Multi-Artist Release' && this.CREATED_ALBUM && 
              this.CREATED_ALBUM.secondaryArtists && this.CREATED_ALBUM.secondaryArtists.length > 0) {
            
            secondaryArtists = this.CREATED_ALBUM.secondaryArtists.map(artist => ({
              id: artist.id,
              name: artist.name,
              isAlbumArtist: false,
              isSecondaryArtist: true
            }));
            
            console.log(`Including ${secondaryArtists.length} secondary artists from step one:`, 
                      secondaryArtists.map(a => a.name).join(', '));
          }
          
          // Update artists for ALL tracks
          this.prepareUploadTracks.forEach((track, index) => {
            // Handle both old and new format
            if (!track.trackArtists) {
              // Convert from old format
              track.trackArtists = [];
              
              // If track has an existing artist and it's different from album artist,
              // add it as a non-album artist
              if (track.trackArtist && track.trackArtist !== this.mainArtistName) {
                track.trackArtists.push({
                  id: track.trackArtistId || '',
                  name: track.trackArtist,
                  isAlbumArtist: false
                });
              }
            }
            
            // Remove any existing album artists
            let nonAlbumArtists = track.trackArtists.filter(artist => !artist.isAlbumArtist);
            
            // Also remove any artists that match the new album artist ID or secondary artist IDs
            if (this.details.artist.id) {
              nonAlbumArtists = nonAlbumArtists.filter(artist => {
                // Keep artists that don't match main artist ID
                const isMainArtist = artist.id === this.details.artist.id;
                // Keep artists that aren't in the secondary artists list
                const isSecondaryArtist = secondaryArtists.some(secArtist => secArtist.id === artist.id);
                return !isMainArtist && !isSecondaryArtist;
              });
            }
            
            // Add the new album artist and secondary artists
            track.trackArtists = [albumArtist, ...secondaryArtists, ...nonAlbumArtists];
            
            // Update the display string
            track.trackArtist = this.getTrackArtistDisplay(index);
            
            // Mark as changed if it's an existing track
            if (track.id) {
              track._hasMetadataChanges = true;
              this.releaseInfoChanged = true;
            }
          });
        },

        updateEmptyTrackArtistNames() {
          if (!this.mainArtistName) return;

          console.log(`Setting album artist for tracks without artists: ${this.mainArtistName}`);
          
          // Current album artist
          const albumArtist = {
            id: this.details.artist.id,
            name: this.mainArtistName,
            isAlbumArtist: true
          };
          
          // Update artists for tracks that don't have any
          this.prepareUploadTracks.forEach((track, index) => {
            // Check if track has no artists or only empty array
            if (!track.trackArtists || track.trackArtists.length === 0) {
              track.trackArtists = [albumArtist];
              track.trackArtist = this.mainArtistName;
              
              // Mark as changed if it's an existing track
              if (track.id) {
                track._hasMetadataChanges = true;
                this.releaseInfoChanged = true;
              }
            }
          });
        },
        handleStructureSelected(structure) {
          if (this.$refs.releaseInfo) {
            // Reset label error state when structure changes
            this.$refs.releaseInfo.labelError = false;
            
            // Set the new structure
            this.$refs.releaseInfo.releaseStructure = structure;
            
            // Save structure to the component variable for persistence
            this.savedReleaseStructure = structure;
            console.log('Saved structure selection:', structure);
            
            if (structure === 'Various Artists Release') {
              // Set various artists flag to true and clear artist selection
              this.$store.commit('SET_VARIOUS_ARTISTS', true);
              console.log('Various Artists flag set to TRUE for structure:', structure);
              this.$refs.releaseInfo.clearInput('artist');
            } else {
              // Set various artists flag to false for other structure types
              this.$store.commit('SET_VARIOUS_ARTISTS', false);
              console.log('Various Artists flag set to FALSE for structure:', structure);
            }
          }
        },

        // Add handler for structure selection
        handleOpenStructureSelect() {
          console.log('handleOpenStructureSelect called');
          this.structureDialogVisible = true;
          console.log('structureDialogVisible set to:', this.structureDialogVisible);
        },

        handleStructureCleared() {
          console.log('Structure cleared in ReleaseInfo component');
          
          // Clear the saved structure in the parent component
          this.savedReleaseStructure = null;
          
          // Reset the various artists flag
          this.$store.commit('SET_VARIOUS_ARTISTS', false);
        },

        handleReleaseInfoInitialized(data) {
          console.log('ReleaseInfo component initialized with data:', data);
          
          // If we have a saved structure, apply it to the newly initialized component
          if (this.savedReleaseStructure && this.$refs.releaseInfo) {
            console.log('Setting saved structure on initialized ReleaseInfo:', this.savedReleaseStructure);
            this.$refs.releaseInfo.releaseStructure = this.savedReleaseStructure;
          }
          
          // Set artist ID from the component's data
          if (data.details && data.details.artist && data.details.artist.id) {
            this.updateArtistId(data.details.artist.id);
          }
        },

        updateArtistId(artistId) {
          console.log('artistId watcher triggered with:', artistId);
          
          if (!artistId) {
            console.log('Main artist cleared due to null/undefined artistId');
            this.mainArtistName = null;
            return;
          }
          
          // Find the artist from the list
          const foundArtist = this.YOUR_ARTISTS.find(artist => artist.id === artistId);
          
          if (foundArtist) {
            this.mainArtistName = foundArtist.name;
            console.log('Main artist set to:', this.mainArtistName);
            
            // If we're in one artist mode, update all tracks
            if (!this.VARIOUS_ARTISTS) {
              this.updateAllTrackArtistNames();
            }
          }
        },

        onLabelCleared() {
          console.log('Label cleared in ReleaseInfo component');
          // Handle any special logic needed when label is cleared
          this.labelExplicitlyCleared = true;
        },
	}
}
</script>

<style lang="scss">
.interrupted {
    padding: 0!important;
}
.upload_status {
    .delete_track {
        &.delete-process {
            background-image: none!important;
        }
        .v-progress-circular {
            left: 16px!important;
            width: 22px!important;
            height: 22px!important;
        }
    }
}

.error-message {
    color: #ff0000;
    font-size: 14px;
    margin-top: 10px;
    padding: 8px;
    background-color: rgba(255, 0, 0, 0.1);
    border-radius: 4px;
    text-align: center;
}
</style>

